import BlainBaseService from './BlainBaseService';
import { ArticleSummary } from 'Src/js/types/Blog';
import { objectToQueryString } from 'Src/js/utilities/url';

export interface IBlogService {
	getPosts(query: PostQuery): Promise<ArticleSummary[]>;
}

export class BlogService extends BlainBaseService implements IBlogService {
	getPosts(query: PostQuery): Promise<ArticleSummary[]> {
		return this._httpSvc.get(`/bff-blog-posts/?${objectToQueryString(query, { listFormat: 'separate' })}`);
	}
}

/**
 * A collection of constant IDs that correspond to categories in the Blain's WordPress blog. Used to filter posts by category when communicating with the blog API.
 * Can be viewed in wp-admin at https://www.farmandfleet.com/blog/wp-admin/term.php?taxonomy=category&tag_ID=CATEGORY_ID
 */
export const BlogCategories = Object.freeze({
	Automotive: 2,
	FarmLivestock: 1507,
	Pets: 1506,
	HomeFamily: 1508,
});

/**
 * A collection of groups of IDs that correspond to tags in the Blain's WordPress blog. Grouped by use-case; they're not currently useful enough on their own to warrant individual naming. Used to filter by tag(s) when communicating with the blog API.
 * Individual tags can be viewed in wp-admin at https://www.farmandfleet.com/blog/wp-admin/term.php?taxonomy=post_tag&tag_ID=TAG_ID
 */
export const BlogTagGroups = Object.freeze({
	Batteries: [186],
	Tires: [1467],
	ServiceRepair: [1464],
	Horses: [973],
	Dogs: [304],
	Cats: [747],
	Tools: [469],
	Kitchen: [1469],
	HousePets: [747, 306, 1015] // cats, dogs, small animals
});

// I've chosen to keep the query types in this file because they're very closely tied to the getPosts method of IBlogService.
// ArticleSummary lives in the types directory, as it is a model type and is not necessarily specific to this use-case.

export type PostQuery = { // type instead of interface so this is string-indexable for compat with objectToQueryString
	context?: PostQueryContext;
	page?: number;
	perPage?: number;
	search?: string;
	after?: string;
	author?: number[];
	authorExclude?: number[];
	before?: string;
	include?: number[];
	exclude?: number[];
	offset?: number;
	order?: PostQueryOrder;
	orderBy?: PostQueryOrderBy;
	slug?: string[];
	status?: string;
	taxRelation?: PostQueryTaxRelation;
	categories?: number[];
	categoriesExclude?: number[];
	tags?: number[];
	tagsExclude?: number[];
	sticky?: boolean;
}

export enum PostQueryContext {
	View,
	Embed,
	Edit,
}

export enum PostQueryOrder {
	Asc,
	Desc,
}

export enum PostQueryOrderBy {
	Author,
	Date,
	Id,
	Include,
	Modified,
	Parent,
	Relevance,
	Slug,
	IncludeSlugs,
	Title
}

export enum PostQueryTaxRelation {
	And,
	Or,
}
